import React, { useState } from 'react';
import { Mail, Send, CheckCircle2, Facebook, Youtube, MessageSquare } from 'lucide-react';
import { CONTACT } from '@/constants/testIds';
import { CONTACT_EMAIL, SOCIAL } from '@/constants/stations';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const encode = (data) =>
    Object.keys(data)
      .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
      .join('&');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSending(true);
    setError('');
    try {
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({ 'form-name': 'contact', ...form }),
      });
      if (!res.ok) throw new Error('Network response was not ok');
      setSent(true);
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      setError('Sending failed. Please try again or email us directly at ' + CONTACT_EMAIL + '.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid={CONTACT.page} className="pt-28 pb-20">
      <section className="relative py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <div className="text-[10px] tracking-[0.25em] uppercase text-amber font-bold mb-4">
            Say Hello
          </div>
          <h1 className="font-display text-5xl md:text-7xl text-cream leading-[0.95]">
            Drop us a <span className="font-serif-italic italic text-amber">line.</span>
          </h1>
          <p className="mt-6 text-cream/70 font-body text-lg max-w-2xl mx-auto leading-relaxed">
            Song requests, dedications, partnerships, kind words — we read everything. Promise.
          </p>
        </div>
      </section>

      <section className="relative">
        <div className="max-w-5xl mx-auto px-6 md:px-10 grid md:grid-cols-5 gap-10">
          {/* Form */}
          <form
            data-testid={CONTACT.form}
            name="contact"
            method="POST"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            onSubmit={submit}
            className="md:col-span-3 rounded-3xl border border-amber/20 bg-espresso/60 p-8 md:p-10"
          >
            {/* Netlify form fields */}
            <input type="hidden" name="form-name" value="contact" />
            <p className="hidden">
              <label>Don&apos;t fill this out: <input name="bot-field" /></label>
            </p>
            <div className="grid gap-5">
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-amber font-bold block mb-2">Name</label>
                <input
                  data-testid={CONTACT.nameInput}
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="w-full bg-vinyl border border-amber/20 rounded-xl px-4 py-3 text-cream font-body focus:outline-none focus:border-amber focus:ring-2 focus:ring-amber/30 transition"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-amber font-bold block mb-2">Email</label>
                <input
                  data-testid={CONTACT.emailInput}
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="w-full bg-vinyl border border-amber/20 rounded-xl px-4 py-3 text-cream font-body focus:outline-none focus:border-amber focus:ring-2 focus:ring-amber/30 transition"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-amber font-bold block mb-2">Message</label>
                <textarea
                  data-testid={CONTACT.messageInput}
                  rows="6"
                  name="message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  required
                  className="w-full bg-vinyl border border-amber/20 rounded-xl px-4 py-3 text-cream font-body focus:outline-none focus:border-amber focus:ring-2 focus:ring-amber/30 transition resize-none"
                  placeholder="A song request, a dedication, or just a hello..."
                />
              </div>

              {sent ? (
                <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-amber/10 border border-amber/30 text-amber-light font-body text-sm">
                  <CheckCircle2 className="w-4 h-4" />
                  Message sent. Thank you — we&apos;ll reply within a few days.
                </div>
              ) : (
                <>
                  {error && (
                    <div className="px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 font-body text-sm">
                      {error}
                    </div>
                  )}
                  <button
                    data-testid={CONTACT.submitBtn}
                    type="submit"
                    disabled={sending}
                    className="group inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full bg-amber text-vinyl font-body font-bold uppercase tracking-[0.18em] text-sm hover:scale-[1.02] active:scale-95 shadow-[0_15px_35px_-10px_rgba(245,158,11,0.5)] transition-all disabled:opacity-60"
                  >
                    <Send className="w-4 h-4" /> {sending ? 'Sending…' : 'Send Message'}
                  </button>
                </>
              )}
            </div>
          </form>

          {/* Side info */}
          <aside className="md:col-span-2 space-y-6">
            <div className="rounded-3xl border border-amber/15 bg-espresso/40 p-7">
              <Mail className="w-6 h-6 text-amber mb-3" />
              <div className="text-[10px] tracking-[0.25em] uppercase text-amber font-bold mb-2">Email</div>
              <a
                data-testid={CONTACT.emailLink}
                href={`mailto:${CONTACT_EMAIL}`}
                className="block font-body font-semibold text-cream hover:text-amber transition whitespace-nowrap text-sm sm:text-base tracking-tight"
                title={CONTACT_EMAIL}
              >
                {CONTACT_EMAIL}
              </a>
            </div>

            <div className="rounded-3xl border border-amber/15 bg-espresso/40 p-7">
              <MessageSquare className="w-6 h-6 text-amber mb-3" />
              <div className="text-[10px] tracking-[0.25em] uppercase text-amber font-bold mb-3">Stay in touch</div>
              <div className="flex items-center gap-3">
                <a
                  data-testid="contact-facebook-link"
                  href={SOCIAL.facebook}
                  target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-amber/20 flex items-center justify-center text-cream/80 hover:text-amber hover:border-amber transition"
                  aria-label="Facebook"
                >
                  <Facebook className="w-4 h-4" />
                </a>
                <a
                  data-testid="contact-youtube-link"
                  href={SOCIAL.youtube}
                  target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-amber/20 flex items-center justify-center text-cream/80 hover:text-amber hover:border-amber transition"
                  aria-label="YouTube"
                >
                  <Youtube className="w-4 h-4" />
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default Contact;
